"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
require("whatwg-fetch");
const uuid_1 = require("uuid");
const Event_1 = require("./Event");
const package_json_1 = require("../package.json");
class Collector {
    organizationId;
    identifierCookieScope;
    uniqueRequestIDKey;
    endpointBaseUrl;
    libraryVersion;
    cookieDomain;
    cookiePathScope;
    optOutCookieKey;
    sessionDurationSeconds;
    clientId;
    cookies;
    sessionId;
    identity;
    testMode;
    testModeCookieKey;
    initializedAt;
    trackingStatus = 'no_decision';
    constructor(params) {
        if (!params.organizationId) {
            throw new Error('error: no organizationId passed. initialize with `{ organizationId: "your organizationId here" }`');
        }
        this.organizationId = params.organizationId;
        this.identifierCookieScope = params.identifierCookieScope || [];
        this.uniqueRequestIDKey = params.uniqueRequestIDKey || 'x-cf-request-id';
        this.sessionDurationSeconds = params.sessionDurationSeconds || (60 * 30);
        this.endpointBaseUrl = params.endpointBaseUrl;
        this.libraryVersion = package_json_1.version;
        this.cookies = {};
        this.sessionId = '';
        this.identity = {};
        this.clientId = `${params.organizationId}_client_id`;
        this.initializedAt = new Date();
        this.optOutCookieKey = params.optOutCookieKey || `${params.organizationId}_tracking_decision`;
        this.testModeCookieKey = params.testModeCookieKey || `${params.organizationId}_test_mode`;
        this.testMode = params.testMode || false;
        this.cookieDomain = params.cookieDomain || this.guessDomainName();
        this.cookiePathScope = params.cookiePathScope || '/';
        this.initializeWindow();
        this.initializeSession();
    }
    initializeWindow = () => {
        window.cc = window.cc || { event_queue: [] };
        window.cc.event_queue = window.cc.event_queue || [];
        if (!this.queueInitialized()) {
            window.cc.event_queue = new Proxy(window.cc.event_queue, {
                set: (target, prop, value) => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    // eslint-disable-next-line no-param-reassign
                    target[prop] = value;
                    if (prop !== 'length') {
                        this.trackCommand(value);
                    }
                    return true;
                },
            });
            window.cc.queue_initialized = true;
        }
    };
    queueInitialized = () => (window.cc?.queue_initialized || false);
    runEventQueue = () => {
        window.cc?.event_queue?.forEach((command) => {
            this.trackCommand(command);
        });
    };
    getCookies = () => {
        const rawCookies = window.document.cookie;
        if (rawCookies.length > 0) {
            const cookieArray = rawCookies.split('; ');
            if (cookieArray.length >= 0) {
                cookieArray.forEach((cookiePair) => {
                    const cookieParts = cookiePair.split('=');
                    const key = cookieParts.shift();
                    const value = cookieParts.join('=');
                    if (!key) {
                        return;
                    }
                    if (!this.cookies[key]) {
                        this.cookies[key] = value;
                    }
                });
            }
        }
    };
    // expireMinutes defaults to 1 year
    setCookie = (key, value, expireMinutes = (365 * 60 * 24)) => {
        this.cookies[key] = value;
        const date = new Date();
        date.setTime(date.getTime() + (expireMinutes * 60 * 1000));
        const cookie = `${key}=${value};expires=${date.toUTCString()};SameSite=Lax${this.cookieDomain ? `;Domain=${this.cookieDomain}` : ''};Path=${this.cookiePathScope}`;
        window.document.cookie = cookie;
    };
    getCookie = (key) => {
        this.getCookies();
        return this.cookies[key];
    };
    guessDomainName = () => {
        const domainParts = window.location.host.split('.');
        if (domainParts.length >= 2) {
            return domainParts.slice(-2).join('.');
        }
        return undefined;
    };
    addIdentifier = (key, value) => {
        this.identity[key] = { key, value };
        if (key === this.clientId) {
            this.identity.client_id = { key, value };
            this.setCookie(key, value, (365 * 60 * 24));
        }
    };
    initializeSession = () => {
        this.getCookies();
        if (this.cookies[this.testModeCookieKey] === 'true') {
            this.testMode = true;
        }
        if (this.cookies[this.optOutCookieKey] === 'opt_out') {
            this.trackingStatus = 'opt_out';
        }
        else {
            const cookie = this.getCookie(this.clientId);
            if (!cookie) {
                const uniqueID = (0, uuid_1.v1)();
                const expireDays = 365;
                this.setCookie(this.clientId, uniqueID, (expireDays * 60 * 24));
                this.addIdentifier(this.clientId, uniqueID);
            }
            else {
                this.addIdentifier(this.clientId, cookie);
            }
            this.identifierCookieScope.forEach((scopedCookie) => {
                if (scopedCookie) {
                    this.addIdentifier(scopedCookie, this.cookies[scopedCookie]);
                }
            });
            // eslint-disable-next-line camelcase
            const { session_id } = this.cookies;
            // eslint-disable-next-line camelcase
            if (session_id) {
                // eslint-disable-next-line camelcase
                this.sessionId = session_id;
            }
            else {
                this.sessionId = (0, uuid_1.v1)();
            }
            this.setCookie('session_id', this.sessionId, 30);
        }
        window.addEventListener('unload', () => {
            this.track({ action: 'page_exit' }, 'beacon');
        });
        this.initializeWindow();
        this.runEventQueue();
    };
    trackCommand = (command) => {
        switch (command.cmd) {
            case 'track': {
                this.track(command.data, command.transport || 'xhr');
                return;
            }
            case 'identify': {
                const { key, value } = command.data;
                if (!key || !value) {
                    throw Error('identifier does not match pattern { "key": "<your key>", "value": "<your value>" }');
                }
                this.addIdentifier(key, value);
                return;
            }
            default: {
                throw Error('unexpected command');
            }
        }
    };
    track = (rawEvent, transport = 'xhr') => {
        this.getCookies();
        const now = new Date();
        const event = {
            timestamp: rawEvent.timestamp || now,
            organizationId: rawEvent.organizationId || this.organizationId || window.cc?.organization_id,
            experimentId: window.cc?.experiment_id,
            variantKeyName: window.cc?.variant_key_name,
            pageId: window.cc?.page_id,
            cohortId: window.cc?.cohort_id,
            clientSideDecisionId: window.cc?.client_side_decision_id,
            featureFlags: window.cc?.feature_flags,
            decisionId: window.cc?.decision_id,
            referrerUrl: window.document.referrer,
            url: window.document.URL,
            userAgent: window.navigator.userAgent,
            eventSource: 'web',
            trackingStatus: this.trackingStatus,
            pageHeightPixels: String(document.documentElement.clientHeight),
            pageWidthPixels: String(document.documentElement.clientWidth),
            pageScrollDepth: String(window.scrollY),
            browserLocale: window.navigator.languages.join(','),
            clientLibraryVersion: this.libraryVersion,
            timezoneOffsetHours: now.getTimezoneOffset() / -60,
            timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
            ...rawEvent,
        };
        if (this.testMode) {
            event.isTest = this.testMode;
        }
        if (this.trackingStatus !== 'opt_out') {
            if (rawEvent.userId) {
                this.addIdentifier('user_id', rawEvent.userId);
            }
            if (rawEvent.email) {
                this.addIdentifier('email', rawEvent.email);
            }
            event.email = this.identity?.email?.value;
            event.userId = this.identity?.user_id?.value;
            event.clientId = this.identity[this.clientId]?.value;
            event.cookies = this.cookies;
            event.rawCookies = window.document.cookie;
            event.identity = this.identity;
            event.sessionId = this.sessionId;
        }
        if (transport === 'beacon' && window.navigator?.sendBeacon) {
            event.trackingTransport = 'beacon';
            const eventPayload = (0, Event_1.buildEventPayload)(event, 'collector');
            try {
                window.navigator.sendBeacon(`${this.endpointBaseUrl}/api/v1/events/`, JSON.stringify(eventPayload));
            }
            catch (e) {
                window.fetch(`${this.endpointBaseUrl}/api/v1/events/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(eventPayload),
                });
                // eslint-disable-next-line no-console
                console.error(e);
            }
        }
        else {
            event.trackingTransport = 'fetch';
            const eventPayload = (0, Event_1.buildEventPayload)(event, 'collector');
            window.fetch(`${this.endpointBaseUrl}/api/v1/events/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(eventPayload),
            });
        }
    };
}
exports.default = Collector;
